// Delivery bar — FE-032 / spec §7.0.2 + dashboard parity v3.7.
//
// Ahead-vs-behind track record over the last 30 days of completed
// Commitments. v3.7 flips this to vertical stacked columns — sage
// (ahead) sits at the bottom, ember (behind) stacks on top, on-time
// percent above, staff name below. Pagination matches the shared
// HorizontalBar primitive (6 employees per page).
//
// Behind segments still drill through to the Commitment Tracker
// filtered to that staffer's late finishes.

import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import {
  buildCommitmentFilterUrl,
  type DashboardDateRange,
  type EmployeeAnalyticsResponse,
} from "@/features/dashboard";
import { formatStaffName } from "@/features/dashboard/staffPerformance";

import { DashboardSectionHeader } from "../primitives";


interface DeliveryBarProps {
  rows: EmployeeAnalyticsResponse["delivery_bar"];
  dateRange: DashboardDateRange;
}

const PAGE_SIZE = 6;
const COLUMN_HEIGHT = 160; // px


export function DeliveryBar({ rows, dateRange }: DeliveryBarProps) {
  const sorted = [...rows].sort(
    (a, b) => b.ahead_percent - a.ahead_percent,
  );
  const [page, setPage] = useState(0);
  useEffect(() => {
    setPage(0);
  }, [sorted.length]);

  const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const start = safePage * PAGE_SIZE;
  const visible = sorted.slice(start, start + PAGE_SIZE);
  const showPager = pageCount > 1;

  return (
    <section data-testid="delivery-bar" aria-labelledby="delivery-bar-heading">
      <DashboardSectionHeader
        title={
          <span id="delivery-bar-heading" className="text-sm font-semibold">
            Delivery
          </span>
        }
        subtitle="Ahead-of-due vs. behind-of-due in the last 30 days"
      />
      <div className="mt-2">
        {sorted.length === 0 ? (
          <p
            data-testid="delivery-bar-empty"
            className="text-sm text-text-tertiary"
          >
            No completed Commitments in the last 30 days.
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            <ul
              role="list"
              className="flex items-end justify-around gap-3 rounded-md bg-canvas/40 px-3 pt-3 pb-2"
              style={{ minHeight: COLUMN_HEIGHT + 72 }}
            >
              {visible.map((row) => {
                const total = row.ahead_count + row.behind_count;
                const aheadPct = row.ahead_percent;
                const behindPct = total === 0 ? 0 : 100 - aheadPct;
                const behindHref = buildCommitmentFilterUrl(
                  { assigned_to: row.user_id, on_time: false },
                  dateRange,
                );
                const name = formatStaffName(row);
                return (
                  <li
                    key={row.user_id}
                    className="flex min-w-0 flex-1 flex-col items-center gap-1"
                  >
                    <div
                      data-testid={`delivery-row-${row.user_id}`}
                      aria-label={
                        `${name}: ${row.ahead_count} ahead, ` +
                        `${row.behind_count} behind (${aheadPct}% on time)`
                      }
                      className="flex w-full flex-col items-center gap-1"
                    >
                      {/* On-time % above the column */}
                      <span className="text-xs font-medium text-text-primary tabular-nums">
                        {aheadPct.toFixed(0)}%
                        <span className="ml-0.5 text-[10px] text-text-tertiary">
                          on time
                        </span>
                      </span>
                      {/* Vertical stacked column: ember on top,
                          sage at bottom — flex-col-reverse keeps the
                          DOM order ahead→behind so testid order is
                          stable for assertions. */}
                      <span
                        className="relative flex w-full flex-col-reverse overflow-hidden rounded-t-md bg-recessed"
                        style={{ height: COLUMN_HEIGHT }}
                      >
                        <span
                          data-testid={`delivery-fill-ahead-${row.user_id}`}
                          className="block w-full bg-sage"
                          style={{ height: `${aheadPct}%` }}
                          aria-hidden="true"
                        />
                        <span
                          data-testid={`delivery-fill-behind-${row.user_id}`}
                          className="block w-full bg-ember"
                          style={{ height: `${behindPct}%` }}
                          aria-hidden="true"
                        />
                      </span>
                      {/* Name underneath as the X-axis label */}
                      <span
                        className="w-full truncate text-center text-[11px] text-text-secondary"
                        title={name}
                      >
                        {name}
                      </span>
                    </div>
                    {row.behind_count > 0 && (
                      <Link
                        to={behindHref}
                        data-testid={`delivery-behind-link-${row.user_id}`}
                        className="text-[10px] text-teal hover:underline"
                      >
                        View {row.behind_count} late
                        {row.behind_count === 1 ? "" : "s"}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
            {showPager && (
              <footer
                data-testid="delivery-bar-pager"
                className="flex items-center justify-between text-[11px] text-text-tertiary"
              >
                <span>
                  {start + 1}–{start + visible.length} of {sorted.length}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    data-testid="delivery-bar-prev"
                    aria-label="Previous staff"
                    disabled={safePage === 0}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    className="inline-flex h-6 w-6 items-center justify-center rounded text-text-secondary hover:bg-recessed disabled:cursor-not-allowed disabled:text-text-tertiary"
                  >
                    <ChevronLeft size={14} aria-hidden="true" />
                  </button>
                  <span className="tabular-nums">
                    {safePage + 1} / {pageCount}
                  </span>
                  <button
                    type="button"
                    data-testid="delivery-bar-next"
                    aria-label="Next staff"
                    disabled={safePage >= pageCount - 1}
                    onClick={() =>
                      setPage((p) => Math.min(pageCount - 1, p + 1))
                    }
                    className="inline-flex h-6 w-6 items-center justify-center rounded text-text-secondary hover:bg-recessed disabled:cursor-not-allowed disabled:text-text-tertiary"
                  >
                    <ChevronRight size={14} aria-hidden="true" />
                  </button>
                </div>
              </footer>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
